import { addDays, differenceInDays, format as formatDate, isAfter, isEqual, parse, parseISO } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { DURATION_IN_DAYS_THRESHOLD } from "@/constants.js";
import { parseSessionEndDate } from "@/mixins/helpers";

export function durationLabel({ startDate, endDate }) {
  const format = "MMMM d, yyyy";
  if (startDate && endDate) {
    const fmtStartDate = formatDate(parse(startDate, "yyyy-MM-dd", new Date()), format);
    const fmtEndDate = formatDate(parse(endDate, "yyyy-MM-dd", new Date()), format);

    return `${fmtStartDate} – ${fmtEndDate}`;
  } else {
    return "";
  }
}

export function getTravelDetails(session) {
  const travelDetails = session?.session_travel_details ?? [];

  const sortedAsc = travelDetails.sort((a, b) => Number(new Date(a.arrival_date)) - Number(new Date(b.arrival_date)));
  const arrivalDetails = sortedAsc.length ? sortedAsc[0] : {};

  const sortedDesc = travelDetails.sort(
    (a, b) => Number(new Date(b.departure_date)) - Number(new Date(a.departure_date)),
  );
  const departureDetails = sortedDesc.length ? sortedDesc[0] : {};

  return {
    city: arrivalDetails?.arrival_city?.city,
    country: arrivalDetails?.arrival_city?.country.name,
    startDate: arrivalDetails?.arrival_date ?? "",
    endDate: departureDetails?.departure_date ?? "",
  };
}

export function stillValid({ startDate, endDate }) {
  if ([null, undefined].includes(startDate) || [null, undefined].includes(endDate)) return true;
  const now = new Date().getTime();
  const start = new Date(startDate).getTime();
  const end = new Date(endDate).getTime();

  return now >= start && now <= end;
}

export const verifyStartDateLowerThenEndDate = (startDate, endDate, parseDateFormat = "yyyy-MM-dd") => {
  if (!startDate && !endDate) {
    return true;
  } else if (!startDate && endDate) {
    return false;
  }
  return (
    !isAfter(parse(startDate, parseDateFormat, new Date()), parse(endDate, parseDateFormat, new Date())) &&
    !isEqual(parse(startDate, parseDateFormat, new Date()), parse(endDate, parseDateFormat, new Date()))
  );
};

export const formatDuration = (durationInDays) => {
  if (durationInDays === null || !Number.isInteger(Number(durationInDays))) {
    return null;
  }
  if (durationInDays < DURATION_IN_DAYS_THRESHOLD) {
    const days = `day${durationInDays > 1 ? "s" : ""}`;
    return `${durationInDays} ${days}`;
  } else {
    const durationInWeeks = Math.ceil(durationInDays / 7);
    const weeks = `week${durationInWeeks > 1 ? "s" : ""}`; // Pluralize 'Week' if necessary
    return `${durationInWeeks} ${weeks}`;
  }
};

export function convertOffSetFromMinutesToHours(offSetInMinutes) {
  const hour = Math.floor(offSetInMinutes / 60);
  const minutes = Math.abs(offSetInMinutes % 60);
  const plusMinus = hour > 0 ? "+" : "-";
  const hourString = Math.abs(hour) < 10 ? `0${Math.abs(hour)}` : `${Math.abs(hour)}`;
  const minutesString = minutes === 0 ? "00" : `${minutes}`;
  return `${plusMinus}${hourString}:${minutesString}`;
}

export function dateTimeIsBeforeToday(time, timeZoneOffsetInMinutes) {
  const now = new Date();
  let today;
  if (timeZoneOffsetInMinutes) {
    const getOffset = now.getTimezoneOffset();
    const offsetDifference = timeZoneOffsetInMinutes + getOffset;
    const timeInMilliseconds = now.getTime() + offsetDifference * 60000;
    const adjustedDate = new Date(timeInMilliseconds);
    today = formatDate(adjustedDate, "yyyy-MM-dd HH:mm");
  } else {
    today = formatDate(now, "yyyy-MM-dd HH:mm");
  }
  const date_time = formatDate(time, "yyyy-MM-dd HH:mm");
  return date_time < today;
}

export function dateIsBeforeToday(date) {
  const today = formatDate(new Date(), "yyyy-MM-dd");
  const format_date = formatDate(date, "yyyy-MM-dd");
  return format_date < today;
}

export function endDateTimeIsBeforeStartDate(start_date_time, end_date_time) {
  return end_date_time < start_date_time;
}

export function endDateIsBeforeStartDate(start_date, end_date) {
  const format_start_date = formatDate(start_date, "yyyy-MM-dd");
  const format_end_date = formatDate(end_date, "yyyy-MM-dd");
  return format_end_date < format_start_date;
}

export function compareDates(date1, date2) {
  if (date1 < date2) {
    return 1;
  } else if (date1 > date2) {
    return -1;
  } else {
    return 0;
  }
}

export const formatDateFullMonth = (dateString) => {
  if (!dateString) return undefined;
  const date = parseISO(dateString);
  // timeZone is no longer an option here, when this gets typescripted we'll need to remove
  return formatDate(date, "MMM d, yyyy", { timeZone: "UTC" });
};

export function areDatesIntervalConflicting(start_date, end_date, other_start_date, other_end_date) {
  return (
    (start_date <= other_start_date && other_start_date <= end_date) ||
    (other_start_date <= start_date && start_date <= other_end_date)
  );
}

export const numberOfDaysBetweenDates = (date1, date2) => Math.abs(differenceInDays(date1, date2));

export const formatDateUtc = (dateString, formatStr = "MM/dd/yyyy") => {
  if (!dateString) return undefined;
  const date = utcToZonedTime(parseISO(dateString), "UTC");
  return formatDate(date, formatStr, { timeZone: "UTC" });
};

export const parseAndFormatISODateStringToDate = (text, toFormat) => formatDate(parseISO(text), toFormat);

export const parseAndFormatDateStringToDate = (text, fromFormat = "MM/dd/yyyy", toFormat = "MMM dd, yyyy") =>
  formatDate(parse(text, fromFormat, new Date()), toFormat);

export const endOfDayDate = (date) => {
  if (!date) {
    return new Date();
  }
  const initialDate = date instanceof Date ? date : parseISO(date);
  return addDays(initialDate, 1);
};

export const isSessionInPast = (session) => {
  const endDate = parseSessionEndDate(session?.session_travel_details ?? []);
  const academicEndDate = session?.academic_end_date ? new Date(session.academic_end_date) : undefined;

  if (endDate) {
    return endDate < new Date();
  }
  return !!academicEndDate && academicEndDate < new Date();
};

export const formatDateTo2Digit = (dateStr) =>
  new Date(dateStr).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

export const formatTimeFromDate = (
  date = new Date(),
  timezone = "UTC",
  hour = "numeric",
  minute = "2-digit",
  hour12 = true,
) => {
  try {
    return new Date(date).toLocaleTimeString("en-US", {
      timeZone: timezone,
      hour: hour,
      minute: minute,
      hour12: hour12,
    });
  } catch (e) {
    console.error("Error while trying to get time from date:" + e);
    return "";
  }
};
